import React, { FunctionComponent, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useHistory } from 'react-router-dom';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button, Skeleton, SkeletonItem, makeStyles } from '@fluentui/react-components';
import { EditRegular, ArrowLeftRegular } from '@fluentui/react-icons';
import {
  COMPANY_GROUP_DETAILS_QUERY as query,
  EDIT_COMPANY_GROUP_MUTATION,
} from '../../../utils/statApi/CompanyGroupsApi';
import CompanyGroupUsersPicker from './CompanyGroupUsersPicker';
import CompanyGroupCompanyList from './CompanyGroupCompanyList';
import { ICompanyGroup } from '../../../utils/types/ICompanyGroups';
import FullWidthHeader from '../../common/headers/FullWidthHeader';
import {
  bodyContentContainer,
  bodyContentWrapper,
  iconButtonStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import Subheader from '../../common/headers/Subheader';
import { CompanyGroupEditPanel } from '..';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import CanAccess from '../../common/canAccess/CanAccess';
import { ICompanyGroupIdParams } from '../../common/ParamTypes';

const useStyles = makeStyles({
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
});

const CompanyGroupDetail: FunctionComponent = () => {
  const styles = useStyles();
  const { id } = useParams<ICompanyGroupIdParams>();
  const intId = parseInt(id, 10);
  const { loading, error, data } = useQuery(query, {
    variables: { id: intId },
  });
  const [editMode, setEditMode] = useState(false);
  const { companyGroupDetails } = data || {};
  const history = useHistory();
  const onMutationSuccess = (): void => {
    setEditMode(false);
  };
  const [updateCompanyGroup, { loading: mutationLoading, error: mutationError }] = useMutation(
    EDIT_COMPANY_GROUP_MUTATION,
    {
      onCompleted: onMutationSuccess,
    },
  );

  const handleSaveClick = ({
    name,
    description,
    companyGroupsStatUsers,
    companyGroupCompanies,
  }: ICompanyGroup): void => {
    const companyGroup = {
      id: intId,
      name,
      description,
      companyGroupsStatUsers: companyGroupsStatUsers.map((user) => {
        return {
          alias: user.alias,
          email: user.email,
          firstName: user.firstName,
          graphId: user.graphId,
          lastName: user.lastName,
          name: user.name,
          personnelNumber: user.personnelNumber,
        };
      }),
      companyGroupCompanies: companyGroupCompanies.map((company) => {
        return {
          companyCode: company.companyCode,
          companyId: company.companyId,
          entityLegalName: company.entityLegalName,
        };
      }),
    };
    updateCompanyGroup({
      variables: {
        companyGroup,
      },
    });
  };

  const renderHeader = (): JSX.Element => (
    <Stack horizontal>
      <h1>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular color={iconButtonStyles.color} />}
          style={{ marginRight: 10 }}
          title="Back to company groups list"
          aria-label="Back to company groups list"
          onClick={(): void => {
            history.push(`/companygroups/`);
          }}
        />
        {companyGroupDetails?.name}
      </h1>
      {companyGroupDetails?.name && (
        <CanAccess requestedAction="CompanyGroup:Edit">
          <Button
            appearance="subtle"
            icon={<EditRegular color={iconButtonStyles.color} />}
            title="Edit Company Group"
            aria-label="Edit Company Group"
            onClick={(): void => {
              setEditMode(!editMode);
            }}
          />
        </CanAccess>
      )}
    </Stack>
  );
  const renderSubtitle = (): JSX.Element => {
    if (companyGroupDetails?.description) {
      return <Subheader title={companyGroupDetails?.description} />;
    }
    return null;
  };
  return (
    <>
      <FullWidthHeader title={renderHeader} subtitle={renderSubtitle} />
      <div className={`${bodyContentContainer}  ms-depth-4`}>
        <div className={bodyContentWrapper}>
          <LoadingErrorMessage
            loading={loading}
            error={error}
            actionName="loading the company group data"
          />
          {loading && !error && (
            <Skeleton className={styles.skeleton} aria-label="Loading company group details">
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
            </Skeleton>
          )}
          <Stack>
            {companyGroupDetails && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <CompanyGroupUsersPicker
                    isDisabled
                    selectedItems={companyGroupDetails.companyGroupsStatUsers}
                    ariaLabelId="peoplePickerDetail"
                  />
                </div>

                <CompanyGroupCompanyList companies={companyGroupDetails.companyGroupCompanies} />
              </>
            )}
            {editMode && (
              <CompanyGroupEditPanel
                mutationLoading={mutationLoading}
                onSaveClick={handleSaveClick}
                companyGroup={companyGroupDetails}
                closePanel={(): void => {
                  setEditMode(!editMode);
                }}
              >
                <LoadingErrorMessage
                  loading={mutationLoading}
                  error={mutationError}
                  actionName="updating the company group data"
                />
              </CompanyGroupEditPanel>
            )}
          </Stack>
        </div>
      </div>
    </>
  );
};

export default CompanyGroupDetail;
